/**
 * Remembering WHEN a Facebook ad click happened (#4045).
 *
 * Meta's `fbc` value is `fb.1.<click_time_ms>.<fbclid>` and wants the time of the
 * CLICK — not the time we later report the purchase. The trial-to-paid conversion
 * fires about 14 days after the click, so reporting "now" there would tell Meta
 * the click happened two weeks after it really did, and the ad would not be
 * credited. `UrlQueryService` captures the time here and carries it to the server
 * as `fbclid_ts`; the server builds the `fbc` value from it.
 *
 * A pure module with no Angular imports, so it unit-tests under plain node — the
 * same split `text-locale.ts` uses. `UrlQueryService` is the thin adapter that
 * supplies the real `sessionStorage` and clock.
 */

/** Where the click id itself is kept for the session. */
export const CLICK_ID_STORAGE_KEY = 'tracking_fbclid';
/** Where the time that click id was first seen is kept, as epoch millis. */
export const CLICK_TIME_STORAGE_KEY = 'tracking_fbclid_first_seen';

/** The bit of the Storage API this needs — narrowed so a test can pass a plain
 *  object instead of standing up a browser. */
export interface ClickTimeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The epoch-millis time this click id was FIRST seen, as a string.
 *
 * The click id stays in the page URL across reloads, so re-stamping "now" on
 * every page load would keep pushing the recorded click time later and later. We
 * store the first time we saw it against the click id itself: a reload gets the
 * original time back, while a genuinely NEW ad click (a different click id)
 * starts fresh.
 *
 * Stored as two plain strings rather than one JSON blob, so there is nothing to
 * parse and therefore no malformed-value case to handle or to swallow.
 */
export function resolveFirstSeenClickTime(
  fbclid: string,
  storage: ClickTimeStorage,
  nowMillis: number,
): string {
  const storedClickId = storage.getItem(CLICK_ID_STORAGE_KEY);
  const storedTime = storage.getItem(CLICK_TIME_STORAGE_KEY);
  // same click id as the stored one → keep the time we first recorded for it
  if (storedClickId === fbclid && storedTime) {
    return storedTime;
  }
  const stamped = String(nowMillis);
  storage.setItem(CLICK_ID_STORAGE_KEY, fbclid);
  storage.setItem(CLICK_TIME_STORAGE_KEY, stamped);
  return stamped;
}
